import {
  useContext,
  useEffect,
  useMemo,
  useState,
  type ChangeEvent,
  type MouseEvent,
  type ReactElement,
} from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  FaArrowRight,
  FaHourglassHalf,
  FaMagnifyingGlass,
  FaTriangleExclamation,
} from "react-icons/fa6";
import Spinner from "../components/Spinner";
import {
  LdsTable,
  LdsTableBody,
  LdsTableCard,
  LdsTableCell,
  LdsTableHead,
  LdsTableHeadCell,
  LdsTableRow,
  LdsTableScroll,
} from "../components/lds/LdsTable";
import ErrorPage from "../components/ErrorPage";
import GenderIcon from "../components/GenderIcon";
import CommittedValidate from "../components/CommitedValidate";
import WaitingValidate from "../components/WaitingValidate";
import { AuthContext } from "../AuthProvider";
import { mapHttpErrorToMessage } from "../utils/mapHttpErrorToMessage";
import { fetchWaitingWithClass } from "../utils/fetchWaitingWithClass";
import { parseToDate } from "../utils/dateUtils";

type Identifier = string | number;

interface ClassroomRelation {
  id?: Identifier;
  className?: string;
  name?: string;
  title?: string;
  current?: boolean;
  isCurrent?: boolean;
  endDate?: unknown;
}

interface WaitingChild {
  id: Identifier;
  firstName?: string;
  lastName?: string;
  gender?: string;
  committed?: boolean;
  waiting?: boolean;
  dateRegistration?: unknown;
  dateBirth?: unknown;
  classrooms?: ClassroomRelation[];
}

interface HttpMappedError {
  message?: string;
  status?: number;
}

function normalizeList(data: unknown): WaitingChild[] {
  if (Array.isArray(data)) return data as WaitingChild[];

  if (data && typeof data === "object") {
    const record = data as Record<string, unknown>;

    for (const key of ["content", "items", "data"]) {
      if (Array.isArray(record[key])) {
        return record[key] as WaitingChild[];
      }
    }
  }

  return [];
}

function getCurrentClasses(child: WaitingChild): ClassroomRelation[] {
  const classrooms = Array.isArray(child.classrooms) ? child.classrooms : [];
  const current = classrooms.filter(
    (classroom) =>
      classroom.current === true || classroom.isCurrent === true
  );

  return current.length > 0
    ? current
    : classrooms.filter((classroom) => !classroom.endDate);
}

function formatDate(value: unknown): string {
  const date = parseToDate(value);
  return date ? date.toLocaleDateString("nl-NL") : "—";
}

function getClassroomLabel(
  classroom: ClassroomRelation,
  fallback: string
): string {
  return (
    classroom.className ||
    classroom.name ||
    classroom.title ||
    fallback
  );
}

export default function ChildrenWaitingWithClass(): ReactElement {
  const { keycloak } = useContext(AuthContext);
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [rows, setRows] = useState<WaitingChild[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | HttpMappedError | null>(null);
  const [searchTerm, setSearchTerm] = useState<string>("");

  useEffect(() => {
    let active = true;

    const load = async (): Promise<void> => {
      try {
        setLoading(true);
        setError(null);

        const data = await fetchWaitingWithClass();

        if (active) {
          setRows(normalizeList(data));
        }
      } catch (caughtError) {
        if (active) {
          setError(mapHttpErrorToMessage(caughtError, t));
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };

    if (keycloak?.token) {
      void load();
    }

    return () => {
      active = false;
    };
  }, [keycloak?.token, t]);

  const filteredRows = useMemo<WaitingChild[]>(() => {
    const normalizedTerm = searchTerm.trim().toLowerCase();

    if (!normalizedTerm) {
      return rows;
    }

    const unknownClass = t(
      "reports.waiting_with_class.unknown_class",
      { defaultValue: "Unknown classroom" }
    );

    return rows.filter((child) => {
      const fullName = `${child.firstName ?? ""} ${child.lastName ?? ""}`
        .trim()
        .toLowerCase();

      const classNames = getCurrentClasses(child)
        .map((classroom) => getClassroomLabel(classroom, unknownClass))
        .join(" ")
        .toLowerCase();

      return (
        fullName.includes(normalizedTerm) ||
        classNames.includes(normalizedTerm)
      );
    });
  }, [rows, searchTerm, t]);

  if (loading) {
    return <Spinner />;
  }

  if (error) {
    const message =
      typeof error === "string" ? error : error.message;
    const status =
      typeof error === "object" ? error.status : undefined;

    return (
      <ErrorPage
        message={message}
        status={status}
        onRetry={() => window.location.reload()}
      />
    );
  }

  return (
    <main className="min-h-full px-4 py-5 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-7xl rounded-[32px] border border-slate-200/80 bg-white/95 p-5 shadow-[0_26px_70px_rgba(15,23,42,0.12)] dark:border-white/10 dark:bg-slate-950/85 sm:p-6">
        <div className="space-y-5">
        <section className="relative overflow-hidden rounded-[28px] border border-indigo-100 bg-gradient-to-r from-indigo-50 via-white to-sky-50 px-5 py-6 shadow-sm dark:border-white/10 dark:from-indigo-950/35 dark:via-slate-950 dark:to-sky-950/30 sm:px-7 sm:py-7">
          <div className="relative z-10 flex min-w-0 items-center gap-5">
            <div className="flex min-w-0 flex-1 items-center gap-5">
              <div className="flex h-20 w-20 shrink-0 items-center justify-center rounded-full border-4 border-white bg-gradient-to-br from-amber-200 to-orange-50 text-3xl text-amber-700 shadow-lg dark:border-slate-900 dark:from-amber-800 dark:to-orange-950 dark:text-amber-100 sm:h-24 sm:w-24">
                <FaHourglassHalf aria-hidden="true" />
              </div>

              <div className="min-w-0">
                <p className="text-xs font-bold uppercase tracking-[0.08em] text-slate-500 dark:text-slate-400">
                  {t("signals.overview.title", { defaultValue: "Signals" })}
                </p>
                <h1 className="mt-1 text-3xl font-bold tracking-tight text-slate-950 dark:text-white">
                  {t("reports.waiting_with_class.name", {
                    defaultValue: "Interest list conflicts",
                  })}
                </h1>
                <p className="mt-2 max-w-2xl text-sm text-slate-600 dark:text-slate-300">
                  {t("reports.waiting_with_class.description", {
                    defaultValue:
                      "Review children on the interest list who already have an active classroom relation.",
                  })}
                </p>

                <div className="mt-4 flex flex-wrap gap-2">
                  <span className="inline-flex rounded-full border border-amber-200 bg-amber-50 px-3 py-1 text-xs font-semibold text-amber-700 dark:border-amber-400/30 dark:bg-amber-400/10 dark:text-amber-200">
                    {t("reports.waiting_with_class.total", {
                      count: rows.length,
                      defaultValue: "{{count}} children",
                    })}
                  </span>
                  {searchTerm.trim() ? (
                    <span className="inline-flex rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs font-semibold text-slate-600 dark:border-white/10 dark:bg-white/5 dark:text-slate-300">
                      {t("common.results", {
                        count: filteredRows.length,
                        defaultValue: "{{count}} results",
                      })}
                    </span>
                  ) : null}
                </div>
              </div>
            </div>
          </div>

          <div
            className="pointer-events-none absolute inset-y-0 right-5 hidden items-center lg:flex"
            aria-hidden="true"
          >
            <FaHourglassHalf className="text-[110px] text-amber-200/70 dark:text-amber-800/40" />
          </div>
        </section>

        <section className="rounded-[24px] border border-slate-200 bg-white p-4 shadow-sm dark:border-white/10 dark:bg-slate-900/80">
          <label
            htmlFor="waiting-with-class-search"
            className="mb-2 block text-xs font-bold uppercase tracking-[0.1em] text-slate-500 dark:text-slate-400"
          >
            {t("common.search", { defaultValue: "Search" })}
          </label>

          <div className="relative">
            <FaMagnifyingGlass
              aria-hidden="true"
              className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400"
            />

            <input
              id="waiting-with-class-search"
              type="search"
              value={searchTerm}
              onChange={(event: ChangeEvent<HTMLInputElement>) =>
                setSearchTerm(event.target.value)
              }
              placeholder={t("waiting-list.search_placeholder", {
                defaultValue: "Search by child or classroom",
              })}
              className="w-full rounded-2xl border border-slate-200 bg-white py-3 pl-11 pr-4 text-sm text-slate-950 outline-none transition placeholder:text-slate-400 focus:border-indigo-300 focus:ring-4 focus:ring-indigo-100 dark:border-white/10 dark:bg-slate-950/50 dark:text-white dark:placeholder:text-slate-500 dark:focus:border-indigo-400 dark:focus:ring-indigo-400/10"
            />
          </div>
        </section>

        {filteredRows.length === 0 ? (
          <section className="rounded-[28px] border border-dashed border-slate-200 bg-white p-12 text-center shadow-sm dark:border-white/10 dark:bg-slate-900/80">
            <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-amber-100 text-xl text-amber-700 dark:bg-amber-950 dark:text-amber-300">
              <FaTriangleExclamation aria-hidden="true" />
            </div>

            <h2 className="mt-4 text-xl font-bold text-slate-950 dark:text-white">
              {t("reports.waiting_with_class.none_title", {
                defaultValue: "No conflicts found",
              })}
            </h2>

            <p className="mx-auto mt-2 max-w-lg text-sm text-slate-600 dark:text-slate-300">
              {t("reports.waiting_with_class.none", {
                defaultValue:
                  "No children match the current search or there are no interest list conflicts.",
              })}
            </p>
          </section>
        ) : (
          <LdsTableCard className="rounded-[28px]">
            <LdsTableScroll>
              <LdsTable>
                <LdsTableHead>
                  <tr>
                    <LdsTableHeadCell>
                      {t("children.table.gender", {
                        defaultValue: "Gender",
                      })}
                    </LdsTableHeadCell>
                    <LdsTableHeadCell>
                      {t("reports.child_name", {
                        defaultValue: "Child",
                      })}
                    </LdsTableHeadCell>
                    <LdsTableHeadCell>
                      {t(
                        "reports.waiting_with_class.current_classes",
                        { defaultValue: "Current classrooms" }
                      )}
                    </LdsTableHeadCell>
                    <LdsTableHeadCell>
                      {t(
                        "children.common.date_of_registration",
                        { defaultValue: "Registration date" }
                      )}
                    </LdsTableHeadCell>
                    <LdsTableHeadCell>
                      {t("children.common.date_of_birth", {
                        defaultValue: "Date of birth",
                      })}
                    </LdsTableHeadCell>
                    <LdsTableHeadCell className="text-right">
                      {t("children.table.actions", {
                        defaultValue: "Actions",
                      })}
                    </LdsTableHeadCell>
                  </tr>
                </LdsTableHead>

                <LdsTableBody>
                  {filteredRows.map((child) => {
                    const currentClasses = getCurrentClasses(child);
                    const unknownClass = t(
                      "reports.waiting_with_class.unknown_class",
                      { defaultValue: "Unknown classroom" }
                    );

                    return (
                      <LdsTableRow
                        key={String(child.id)}
                        className="cursor-pointer"
                        onClick={() =>
                          navigate(
                            `/family/children/${child.id}/details`
                          )
                        }
                      >
                        <LdsTableCell>
                          <GenderIcon gender={child.gender} />
                        </LdsTableCell>

                        <LdsTableCell>
                          <button
                            type="button"
                            className="text-left font-semibold text-indigo-700 transition hover:text-indigo-500 dark:text-indigo-300 dark:hover:text-indigo-200"
                            onClick={(
                              event: MouseEvent<HTMLButtonElement>
                            ) => {
                              event.stopPropagation();
                              navigate(
                                `/family/children/${child.id}/details`
                              );
                            }}
                          >
                            {[child.firstName, child.lastName]
                              .filter(Boolean)
                              .join(" ")}
                          </button>

                          <span className="ml-2 inline-flex items-center gap-1 align-middle">
                            <CommittedValidate
                              committed={child.committed}
                            />
                            <WaitingValidate waiting={child.waiting} />
                          </span>
                        </LdsTableCell>

                        <LdsTableCell>
                          {currentClasses.length > 0
                            ? currentClasses
                                .map((classroom) =>
                                  getClassroomLabel(
                                    classroom,
                                    unknownClass
                                  )
                                )
                                .join(", ")
                            : unknownClass}
                        </LdsTableCell>

                        <LdsTableCell>
                          {formatDate(child.dateRegistration)}
                        </LdsTableCell>

                        <LdsTableCell>
                          {formatDate(child.dateBirth)}
                        </LdsTableCell>

                        <LdsTableCell className="text-right">
                          <button
                            type="button"
                            className="inline-flex items-center gap-2 rounded-full border border-indigo-200 bg-indigo-50 px-3 py-1.5 text-sm font-semibold text-indigo-700 transition hover:border-indigo-300 hover:bg-indigo-100 dark:border-indigo-500/40 dark:bg-indigo-950/60 dark:text-indigo-200 dark:hover:bg-indigo-900/70"
                            onClick={(
                              event: MouseEvent<HTMLButtonElement>
                            ) => {
                              event.stopPropagation();
                              navigate(
                                `/family/children/${child.id}/details`
                              );
                            }}
                          >
                            {t("common.actions.view", {
                              defaultValue: "View",
                            })}
                            <FaArrowRight
                              aria-hidden="true"
                              className="text-xs"
                            />
                          </button>
                        </LdsTableCell>
                      </LdsTableRow>
                    );
                  })}
                </LdsTableBody>
              </LdsTable>
            </LdsTableScroll>
          </LdsTableCard>
        )}
        </div>
      </div>
    </main>
  );
}
